import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  getSeerbitWalletDetail,
  toggleSeerbitWallet,
  setSeerbitWalletFreeze,
  setSeerbitMerchantAccount,
  removeSeerbitMerchantAccount,
} from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function SeerbitWalletDetail() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { userId } = useParams();
  const detail = useSelector((s) => s.profile.seerbitWalletDetail);

  const [toggling, setToggling] = useState(false);
  const [txSearch, setTxSearch] = useState("");
  const [txFilter, setTxFilter] = useState("all");
  const [freezeReason, setFreezeReason] = useState("");
  const [merchantForm, setMerchantForm] = useState({ publicKey: "", secretKey: "", merchantId: "" });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { dispatch(getSeerbitWalletDetail(userId)); }, [dispatch, userId]);

  const handleToggle = async () => {
    setToggling(true);
    await dispatch(toggleSeerbitWallet(userId));
    dispatch(getSeerbitWalletDetail(userId));
    setToggling(false);
  };

  // Run an admin action, show its message and refresh the wallet
  const run = async (action) => {
    setBusy(true);
    setNotice(null);
    const res = await dispatch(action);
    setBusy(false);
    setNotice({ error: !res?.success, text: res?.message || (res?.success ? "Done" : "Something went wrong") });
    if (res?.success) dispatch(getSeerbitWalletDetail(userId));
    return res;
  };

  const handleFreeze = async (frozen) => {
    if (frozen && !freezeReason.trim()) return setNotice({ error: true, text: "Enter a reason to freeze this wallet" });
    const res = await run(setSeerbitWalletFreeze(userId, frozen, freezeReason.trim()));
    if (res?.success) setFreezeReason("");
  };

  const handleMerchantSave = async () => {
    if (!merchantForm.publicKey.trim() || !merchantForm.secretKey.trim()) {
      return setNotice({ error: true, text: "Public key and secret key are required" });
    }
    const res = await run(setSeerbitMerchantAccount(userId, merchantForm));
    if (res?.success) setMerchantForm({ publicKey: "", secretKey: "", merchantId: "" });
  };

  const handleMerchantRemove = async () => {
    if (!window.confirm("Disconnect this business's merchant account? Their POS will stop working.")) return;
    run(removeSeerbitMerchantAccount(userId));
  };

  if (!detail) {
    return (
      <div style={S.loading}>
        <div style={S.spinner} />
        <p style={{ color: "#888", marginTop: 16, fontSize: 14 }}>Loading wallet…</p>
      </div>
    );
  }

  const { wallet, profile, summary } = detail;
  const allTx = wallet?.transactions || [];
  const filtered = allTx.filter((tx) => {
    const q = txSearch.toLowerCase();
    const matchSearch = !q ||
      (tx.narration || "").toLowerCase().includes(q) ||
      (tx.reference || "").toLowerCase().includes(q) ||
      (tx.sender?.accountName || "").toLowerCase().includes(q) ||
      (tx.recipient?.accountName || "").toLowerCase().includes(q);
    const matchFilter = txFilter === "all" || (txFilter === "pos" ? tx.channel === "pos" : tx.type === txFilter);
    return matchSearch && matchFilter;
  });

  // Settled wallet movements only — POS sales settle to the business merchant account
  const settled = allTx.filter((t) => t.status === "success" && t.channel !== "pos");
  const totalCredit = settled.filter((t) => t.type === "credit").reduce((s, t) => s + (t.amount || 0), 0);
  const totalDebit = settled.filter((t) => t.type === "debit").reduce((s, t) => s + (t.amount || 0), 0);

  const fmtMoney = (k) => k != null ? `₦${(k / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const businessStatus = wallet?.business?.status || "none";
  const merchant = wallet?.merchantAccount || {};
  const frozenLabel = wallet?.frozen
    ? wallet.frozenReason === "balance_limit"
      ? `Frozen — needs ${wallet.requiredTier === "business" ? "Business" : `Tier ${wallet.requiredTier}`}`
      : "Frozen by admin"
    : "Not frozen";

  const stats = [
    { label: "Account Number", value: wallet?.accountNumber || "—", icon: "bi bi-hash", accent: PRIMARY },
    { label: "Bank", value: wallet?.bankName || "—", icon: "bi bi-bank", accent: "#4fb0c6" },
    { label: "Status", value: wallet?.active ? "Active" : "Inactive", icon: "bi bi-circle-fill", accent: wallet?.active ? "#2ec4b6" : "#e63946" },
    { label: "Available Balance", value: fmtMoney(summary?.balance), icon: "bi bi-wallet2", accent: PRIMARY },
    { label: "Freeze", value: frozenLabel, icon: "bi bi-snow", accent: wallet?.frozen ? "#e63946" : "#aaa" },
    { label: "Limits", value: summary?.tierLimits?.label || "—", icon: "bi bi-sliders", accent: "#f4a261" },
    { label: "KYC Tier", value: `Tier ${wallet?.tier || 1}`, icon: "bi bi-shield-check", accent: "#9b5de5" },
    { label: "BVN Verified", value: wallet?.bvnVerified ? "Yes" : "No", icon: "bi bi-person-check", accent: wallet?.bvnVerified ? "#2ec4b6" : "#aaa" },
    { label: "NIN Verified", value: wallet?.ninVerified ? "Yes" : "No", icon: "bi bi-fingerprint", accent: wallet?.ninVerified ? "#2ec4b6" : "#aaa" },
    { label: "Business Status", value: businessStatus === "none" ? "Not applied" : businessStatus, icon: "bi bi-building-check", accent: businessStatus === "approved" ? "#2ec4b6" : "#aaa" },
    { label: "Total Inflow", value: fmtMoney(totalCredit), icon: "bi bi-arrow-down-circle-fill", accent: "#2ec4b6" },
    { label: "Total Outflow", value: fmtMoney(totalDebit), icon: "bi bi-arrow-up-circle-fill", accent: "#e63946" },
  ];

  return (
    <div style={S.root}>
      <button style={S.backBtn} onClick={() => history.push("/admin/seerbit/wallets")}>
        <i className="bi bi-arrow-left" style={{ marginRight: 6 }} />Back to SeerBit Wallets
      </button>

      {/* Merchant header */}
      <div style={S.profileCard}>
        <div style={S.avatar}>
          <i className="bi bi-person-fill" style={{ fontSize: 28, color: PRIMARY }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: BG }}>{profile?.storeInfo?.storeName || profile?.fullname || "—"}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{profile?.email}</div>
        </div>
        <button
          style={{ border: `1.5px solid ${wallet?.active ? "#e63946" : "#2ec4b6"}`, background: wallet?.active ? "rgba(230,57,70,0.06)" : "rgba(46,196,182,0.06)", color: wallet?.active ? "#e63946" : "#2ec4b6", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
          onClick={handleToggle} disabled={toggling}
        >
          {toggling ? "…" : wallet?.active ? "Disable Wallet" : "Enable Wallet"}
        </button>
      </div>

      {notice && (
        <div style={{ ...S.notice, borderColor: notice.error ? "#e63946" : "#2ec4b6", color: notice.error ? "#e63946" : "#2ec4b6" }}>
          {notice.text}
        </div>
      )}

      <div style={S.panels}>
        {/* Freeze */}
        <div style={S.panel}>
          <div style={S.panelTitle}><i className="bi bi-snow" style={{ marginRight: 8 }} />Freeze</div>
          {wallet?.frozen ? (
            <>
              <p style={S.panelText}>
                {wallet.frozenReason === "balance_limit"
                  ? `Balance is above the ${summary?.tierLimits?.label || "tier"} maximum. Transfers resume automatically once the user upgrades.`
                  : `Frozen by admin${wallet.frozenNote ? `: ${wallet.frozenNote}` : ""}.`}
              </p>
              <button style={S.outlineBtn("#2ec4b6")} disabled={busy} onClick={() => handleFreeze(false)}>Unfreeze</button>
            </>
          ) : (
            <>
              <p style={S.panelText}>A frozen wallet can still receive money but cannot transfer out.</p>
              <input style={S.field} placeholder="Reason for freezing" value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} />
              <button style={S.outlineBtn("#e63946")} disabled={busy} onClick={() => handleFreeze(true)}>Freeze wallet</button>
            </>
          )}
        </div>

        {/* Business merchant account */}
        <div style={S.panel}>
          <div style={S.panelTitle}><i className="bi bi-building" style={{ marginRight: 8 }} />Business merchant account (POS)</div>
          {businessStatus !== "approved" ? (
            <p style={S.panelText}>Available after the business application is approved.</p>
          ) : merchant.status === "active" ? (
            <>
              <p style={S.panelText}>
                Connected {merchant.connectedAt ? `on ${fmtDate(merchant.connectedAt)}` : ""}<br />
                Public key: <code>{merchant.publicKey}</code><br />
                {merchant.merchantId && <>Merchant ID: <code>{merchant.merchantId}</code></>}
              </p>
              <button style={S.outlineBtn("#e63946")} disabled={busy} onClick={handleMerchantRemove}>Disconnect</button>
            </>
          ) : (
            <>
              <p style={S.panelText}>
                Create this business's merchant account on SeerBit, then paste its keys. POS payments run and settle on it.
              </p>
              <input style={S.field} placeholder="Public key" value={merchantForm.publicKey} onChange={(e) => setMerchantForm({ ...merchantForm, publicKey: e.target.value })} />
              <input style={S.field} type="password" placeholder="Secret key" value={merchantForm.secretKey} onChange={(e) => setMerchantForm({ ...merchantForm, secretKey: e.target.value })} />
              <input style={S.field} placeholder="Merchant ID (optional)" value={merchantForm.merchantId} onChange={(e) => setMerchantForm({ ...merchantForm, merchantId: e.target.value })} />
              <button style={S.outlineBtn(PRIMARY)} disabled={busy} onClick={handleMerchantSave}>Connect merchant account</button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        {stats.map((s) => (
          <div key={s.label} style={{ ...S.statCard, borderTop: `3px solid ${s.accent}` }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: s.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={s.icon} style={{ color: s.accent, fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: BG }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div style={S.txCard}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: BG, flex: 1 }}>Transactions</span>
          <input style={S.searchInput} placeholder="Search narration, ref…" value={txSearch} onChange={(e) => setTxSearch(e.target.value)} />
          {["all", "credit", "debit", "pos"].map((f) => (
            <button key={f} style={{ border: "1.5px solid #e0e0e0", borderRadius: 20, padding: "5px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, background: txFilter === f ? BG : "#fff", color: txFilter === f ? "#fff" : "#555" }} onClick={() => setTxFilter(f)}>
              {f === "pos" ? "POS" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Amount", "Fee", "Type", "Narration", "Channel", "Counterparty", "Date", "Status"].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No transactions</td></tr>
              ) : filtered.map((tx, i) => {
                const isCredit = tx.type === "credit";
                const party = isCredit ? tx.sender : tx.recipient;
                return (
                  <tr key={tx.reference || i} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 700, color: isCredit ? "#2ec4b6" : "#e63946" }}>
                      {isCredit ? "+" : "−"}{fmtMoney(tx.amount)}
                    </td>
                    <td style={{ ...S.td, color: "#888" }}>
                      {tx.type === "debit" ? fmtMoney(tx.fee || tx.metadata?.fee || 0) : "—"}
                      {tx.type === "debit" && tx.metadata?.platformFee != null && (
                        <div style={{ fontSize: 10, color: "#aaa" }}>Excite {fmtMoney(tx.metadata.platformFee)}</div>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={{ background: isCredit ? "rgba(46,196,182,0.12)" : "rgba(230,57,70,0.1)", color: isCredit ? "#2ec4b6" : "#e63946", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ ...S.td, maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tx.narration || "—"}</td>
                    <td style={S.td}>{tx.channel === "pos" ? `POS${tx.terminalId ? ` · ${tx.terminalId}` : ""}` : tx.channel || "—"}</td>
                    <td style={S.td}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: BG }}>{party?.accountName || "—"}</div>
                      <div style={{ fontSize: 11, color: "#999" }}>{party?.bankName || ""}</div>
                    </td>
                    <td style={{ ...S.td, color: "#888" }}>{fmtDate(tx.paidAt)}</td>
                    <td style={S.td}>
                      <span style={{ background: tx.status === "success" ? "rgba(46,196,182,0.12)" : tx.status === "failed" ? "rgba(230,57,70,0.1)" : "#f5f5f5", color: tx.status === "success" ? "#2ec4b6" : tx.status === "failed" ? "#e63946" : "#aaa", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                        {tx.status || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360 },
  spinner: { width: 36, height: 36, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent" },
  backBtn: { background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 16px", cursor: "pointer", fontSize: 13, marginBottom: 22, display: "inline-flex", alignItems: "center", fontWeight: 500 },
  profileCard: { background: "#fff", borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  avatar: { width: 56, height: 56, borderRadius: "50%", background: "rgba(167,204,72,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginBottom: 24 },
  statCard: { background: "#fff", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  txCard: { background: "#fff", borderRadius: 10, padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  searchInput: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "10px 12px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  notice: { background: "#fff", border: "1.5px solid", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 16 },
  panels: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginBottom: 20 },
  panel: { background: "#fff", borderRadius: 10, padding: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 10 },
  panelTitle: { fontSize: 14, fontWeight: 700, color: BG },
  panelText: { fontSize: 13, color: "#666", margin: 0, lineHeight: 1.6, wordBreak: "break-all" },
  field: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" },
  outlineBtn: (color) => ({ alignSelf: "flex-start", border: `1.5px solid ${color}`, background: "#fff", color, borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13 }),
};
